import React, { useState } from 'react';
import { X, Copy, CheckCircle, MousePointerClick, ShieldCheck, Globe, ExternalLink } from 'lucide-react';
import api from '../api';
import Toast from './Toast';

// Admin setup for the Co-browse Widget: switch it on for the agent, limit it to
// the client's domains, and copy the script tag. Two call interfaces share the
// same settings: the voice widget (look from the agent's Voice Widget theme) and
// the Interactive Avatar Panel (look from its own customizer), for agents with
// LiveAvatar on.
const isTrue = (v) => v === true || v === 'true' || v === 1 || v === '1';

const CobrowseEmbedModal = ({ agent, onClose, onSaved }) => {
  const [enabled, setEnabled] = useState(Boolean(agent.cobrowse_enabled));
  const [domains, setDomains] = useState(agent.cobrowse_allowed_domains || '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);

  const isFlow = agent.agent_type === 'flow';
  const avatarReady = isTrue(agent.liveavatar_enabled);
  const [ui, setUi] = useState('voice');
  const baseUrl = window.location.origin;
  const snippet = ui === 'avatar'
    ? `<script src="${baseUrl}/avatar-panel-v2.js?agent=${agent.id}&cobrowse=1" async></script>`
    : `<script src="${baseUrl}/cobrowse-widget.js?agent=${agent.id}" async></script>`;
  const dirty = enabled !== Boolean(agent.cobrowse_enabled) || domains !== (agent.cobrowse_allowed_domains || '');

  const save = async () => {
    setSaving(true);
    try {
      const payload = { cobrowse_enabled: enabled, cobrowse_allowed_domains: domains.trim() || null };
      await api.put(`/agents/${agent.id}`, payload);
      Object.assign(agent, payload);
      onSaved?.({ ...agent, ...payload });
      setToast({ message: 'Co-browse settings saved', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Could not save settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setToast({ message: 'Failed to copy', type: 'error' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <MousePointerClick className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Co-browse Widget: {agent.name}</h2>
              <p className="text-white/70 text-xs mt-0.5">A voice assistant that can scroll, navigate and fill forms on your site during the call</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isFlow && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-700 dark:text-amber-300">
              Co-browse works with prompt agents only. Conversation flow agents are not supported yet.
            </div>
          )}

          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Allow page control</h3>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                When on, calls from the co-browse widget can scroll the page, open links on the same site, fill form
                fields and submit forms after the caller says yes. Off by default. Other widgets and phone calls are not affected.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              disabled={isFlow}
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-muted'} ${isFlow ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Allowed websites</h3>
            </div>
            <textarea
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              rows={3}
              placeholder={'example.com\nshop.example.org'}
              className="w-full p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground"
            />
            <p className="text-xs text-muted-foreground">
              One domain per line. Each one also covers its subdomains. Leave empty to allow any site, which we do not recommend in production.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground">Call interface</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'voice', label: 'Voice widget', hint: 'A call button and a compact call card' },
                { id: 'avatar', label: 'Interactive Avatar Panel', hint: avatarReady ? 'Live avatar, transcript, voice and typing' : 'Turn LiveAvatar on for this agent first' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  disabled={opt.id === 'avatar' && !avatarReady}
                  onClick={() => setUi(opt.id)}
                  className={`text-left p-3 rounded-lg border transition-colors ${ui === opt.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50'} ${opt.id === 'avatar' && !avatarReady ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="text-sm font-medium text-foreground">{opt.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2.5">
            <h3 className="text-sm font-semibold text-foreground">Install on your website</h3>
            <p className="text-xs text-muted-foreground">
              Add this script tag to every page, for example in your site header or a WordPress header plugin.
              Co-browse needs the script tag. An iframe snippet cannot reach the page.
            </p>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto">
                <pre className="whitespace-pre-wrap break-all">{snippet}</pre>
              </div>
              <button
                type="button"
                onClick={copy}
                className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg flex items-center gap-1.5 text-sm flex-shrink-0 self-start"
              >
                {copied ? <><CheckCircle className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {ui === 'avatar'
                ? 'The panel looks the way it is set up in Interactive Avatar Panel > Customize. Placement and colour options can also go on the script URL.'
                : "Colors, title and button label come from this agent's Voice Widget theme settings."}
            </p>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-xs text-muted-foreground space-y-1.5">
            <div className="flex items-center gap-2 font-semibold text-primary text-sm">
              <ShieldCheck className="w-4 h-4" /> Built-in safeguards
            </div>
            <p>The assistant only uses a fixed set of page actions and never runs code on your site.</p>
            <p>Links stay on your own domain. Password, card, security code and SSN fields are never read or filled.</p>
            <p>Submitting, paying, booking or deleting needs the caller&apos;s spoken yes. Callers can turn page control off at any time.</p>
            <p>Every page action is logged against the call for audit.</p>
            <p>On multi-page sites the call continues on the next page.</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <a
              href={`${baseUrl}/demos/cobrowse/test-site.html?agent=${agent.id}&loader=${encodeURIComponent(baseUrl)}${ui === 'avatar' ? '&ui=avatar' : ''}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Try it on the test site
            </a>
            {ui === 'voice' && (
              <a
                href={`${baseUrl}/embed/cobrowse/${agent.id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open the widget page directly (call only, no page control)
              </a>
            )}
          </div>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CobrowseEmbedModal;
